import { Injectable } from '@angular/core';
import { Flows } from '../app.consts';
import { Router } from '@angular/router';
import {
  fullFlow,
  fastTrackFlow,
  initialScreensFlow,
  bigFlow,
  bigInitialFlow,
  tmSignUpFlow,
  tmAuthFlow
} from 'src/configs/flows/flow.se';
import { TrackingService } from './tracking.service';
import { BasicInfoService } from './basic-info.service';
import { LocaleService } from './locale.service';
import { 
    ApplicationScoreService,
    AppScoreModel,
    SE, FI, NL
} from 'creddo-shared-library';

export interface Flow {
  initialScreen?: FlowSteps;
  questions: Array<FlowSteps>;
  extra: Array<FlowSteps>
}

interface FlowSteps {
  path: string;
  blockTitle?: string;
}

@Injectable()
export class FlowService {
  private readonly initialScreensFlow: Flow;
  private readonly fullFlow: Flow;
  private readonly fastTrackFlow: Flow;
  private readonly bigFlow: Flow;
  private readonly bigInitialFlow: Flow;
  private readonly tmSignUpFlow: Flow;
  private readonly tmAuthFlow: Flow;
  private fullAppScore: AppScoreModel = { screen: [] };
  private fastAppScore: AppScoreModel = { screen: [] };
  private lowAmountAppScore: AppScoreModel = { screen: [] };
  private selectedFlow: Flow = { questions: [], extra: [] };
  private _currentQuestionIndex = 0;
  private currentFlowName!: Flows;

  constructor(
    private router: Router,
    private trackingService: TrackingService,
    private applicationScoreService: ApplicationScoreService,
    private basicInfoService: BasicInfoService,
    private localeService: LocaleService) {
      this.initialScreensFlow = initialScreensFlow;
      this.fullFlow = fullFlow;
      this.fastTrackFlow = fastTrackFlow;
      this.bigFlow = bigFlow;
      this.bigInitialFlow = bigInitialFlow;
      this.tmSignUpFlow = tmSignUpFlow;
      this.tmAuthFlow = tmAuthFlow;
      this.setDomainConfig(this.localeService.locale);
    }

    get currentQuestionIndex(): number {
      return this._currentQuestionIndex + 1;
    }

    get questionsLength(): number {
      return this.selectedFlow.questions.length;
    }

    get flow(): Flow {
      return this.selectedFlow;
    }

    get flowName(): Flows | null {
      return this.currentFlowName || null;
    }

    public selectFlow(flow: Flows): void {
      this.resetQuestionIndex();
      this.trackingService.setFlow(flow);
      this.currentFlowName = flow;
      switch (flow) {
        case Flows.FULL:
          this.selectedFlow = this.fullFlow;
          this.setScoringModel(Flows.FULL);
          break;
        case Flows.FASTTRACK:
          this.selectedFlow = this.fastTrackFlow;
          this.setScoringModel(Flows.FASTTRACK);
          break;
        case Flows.BIG:
          this.selectedFlow = this.bigFlow;
          break;
        case Flows.INITIAL:
          this.selectedFlow = this.initialScreensFlow;
          break;
        case Flows.BIG_INITIAL:
          this.selectedFlow = this.bigInitialFlow;
          break;
        case Flows.TM_SIGN_UP:
          this.selectedFlow = this.tmSignUpFlow;
          break;
        case Flows.TM_AUTH:
          this.selectedFlow = this.tmAuthFlow;
          break;
      }
    }

    public setScoringModel(flow: Flows): void {
      switch(flow) {
        case Flows.FASTTRACK:
          this.applicationScoreService.appScoreObject = this.fastAppScore;
          break;
        case Flows.FULL:
          if (this.localeService.locale === 'sv-SE') {
            this.basicInfoService.amount < 200000 
              ? this.applicationScoreService.appScoreObject = this.lowAmountAppScore 
              : this.applicationScoreService.appScoreObject = this.fullAppScore;
              break;
          } 
          if (this.localeService.locale === 'fi-FI') {
            this.basicInfoService.amount < 20000 
              ? this.applicationScoreService.appScoreObject = this.lowAmountAppScore 
              : this.applicationScoreService.appScoreObject = this.fullAppScore;
              break;
          }
          this.applicationScoreService.appScoreObject = this.fullAppScore;
          break;
      }
    }

    public resetQuestionIndex(): void {
      this._currentQuestionIndex = 0;
    }

    public goToInitialScreen(): void {
      const initialScreen = this.selectedFlow.initialScreen;
      const questions = this.selectedFlow.questions;
      if (initialScreen) {
        this.router.navigate([initialScreen.path]);
      } else {
        this.router.navigate([questions[0].path]);
      }
    }

    public nextScreen(): void {
      const questions = this.selectedFlow.questions;
      const initialScreen = this.selectedFlow.initialScreen?.path || 'non-existing-path';

      if (this.router.url.includes(initialScreen)) {
        this.router.navigate([questions[0].path]);
      } else {
        if (this._currentQuestionIndex + 1 < questions.length) {
          this._currentQuestionIndex += 1;
          this.router.navigate([questions[this._currentQuestionIndex].path]);
        } else {
          this.router.navigate(['summary']);
        }
      }
    }

    public previousScreen(): void {
      if (this._currentQuestionIndex - 1 >= 0) {
        this._currentQuestionIndex -= 1;
        this.router.navigate([this.selectedFlow.questions[this._currentQuestionIndex].path]);
      } else {
        const initialScreen = this.selectedFlow.initialScreen;
        if (initialScreen) {
          this.router.navigate([initialScreen.path]);
        }
      }
    }

    setDomainConfig(locale: string): void {
      if (locale === 'sv-SE') {
        this.fullAppScore = SE.fullAppScore;
        this.fastAppScore = SE.fastAppScore;
        this.lowAmountAppScore = SE.lowAmountAppScore;
      } else if (locale === 'fi-FI') {
        this.fullAppScore = FI.fullAppScore;
        this.fastAppScore = FI.fastAppScore;
        this.lowAmountAppScore = FI.lowAmountAppScore;
      } else if (locale === 'nl-NL') {
        this.fullAppScore = NL.fullAppScore;
        this.fastAppScore = NL.fastAppScore;
        this.lowAmountAppScore = NL.lowAmountAppScore;
      }
    }  
}
